import React, { useState } from "react";
import Page from "../components/Page";
import { 
  useShifts, addNewShift, deleteShift,
  useShiftLogs, addNewShiftLog, deleteShiftLog,
  useStaffTransactions, addNewStaffTransaction, deleteStaffTransaction,
  useStaffPayrollDetails
} from "../controllers/hr.controller";
import { useUsers, processBulkPayroll, resetBulkPayroll, updateUserPayrollStatus } from "../controllers/users.controller";
import { useStoreSettings } from "../controllers/settings.controller";
import { CURRENCIES } from "../config/currencies.config";
import { 
  IconUsers, 
  IconCoin, 
  IconCircleCheck, 
  IconAlertCircle, 
  IconCircleX, 
  IconReload, 
  IconBriefcase,
  IconClock,
  IconCalendar,
  IconPlus,
  IconTrash,
  IconTrendingUp,
  IconTrendingDown,
  IconReportMoney
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";

export default function PayrollPage() {
  const { t } = useTranslation();
  const { data: storeSettings, isLoading: isSettingsLoading } = useStoreSettings();

  // Date state for filters (default to current month)
  const today = new Date();
  const defaultStartDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const defaultEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  
  const [activeTab, setActiveTab] = useState("payroll");
  const [isProcessing, setIsProcessing] = useState(false);

  // SWR Queries
  const { data: staffList, error: payrollError, isLoading: isPayrollLoading, mutate: mutatePayroll } = useStaffPayrollDetails(startDate, endDate);
  const { data: shifts, isLoading: isShiftsLoading, mutate: mutateShifts } = useShifts();
  const { data: shiftLogs, isLoading: isShiftLogsLoading, mutate: mutateShiftLogs } = useShiftLogs(startDate, endDate);
  const { data: transactions, isLoading: isTransactionsLoading, mutate: mutateTransactions } = useStaffTransactions(startDate, endDate);
  const { data: rawUsers } = useUsers(); // For dropdown menus

  // Form states
  const [newShift, setNewShift] = useState({ name: "", startTime: "09:00", endTime: "17:00" });
  const [newLog, setNewLog] = useState({ username: "", shiftId: "", date: new Date().toISOString().split('T')[0] });
  const [newTx, setNewTx] = useState({ username: "", type: "incentive", amount: "", reason: "", date: new Date().toISOString().split('T')[0] });
  const [newAdvance, setNewAdvance] = useState({ username: "", amount: "", reason: "", date: new Date().toISOString().split('T')[0] });

  if (isSettingsLoading || isPayrollLoading || isShiftsLoading || isShiftLogsLoading || isTransactionsLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center min-h-[50vh]">
          <span className="loading loading-spinner loading-lg text-restro-green"></span>
        </div>
      </Page>
    );
  }

  if (payrollError) {
    return (
      <Page>
        <div className="alert alert-error shadow-lg max-w-lg mx-auto mt-10">
          <div>
            <IconCircleX stroke={iconStroke} />
            <span>Error loading HR/Payroll data.</span>
          </div>
        </div>
      </Page>
    );
  }

  const currencyCode = storeSettings?.currency || "USD";
  const currencyObj = CURRENCIES.find((c) => c.cc === currencyCode);
  const currencySymbol = currencyObj?.symbol || "$";

  // Calculations for Summary Cards
  const totalBaseSalary = staffList?.reduce((acc, user) => acc + Number(String(user.base_salary || 0).replace(/,/g, "")), 0) || 0;
  const totalIncentives = staffList?.reduce((acc, user) => acc + Number(String(user.total_incentives || 0).replace(/,/g, "")), 0) || 0;
  const totalFines = staffList?.reduce((acc, user) => acc + Number(String(user.total_fines || 0).replace(/,/g, "")), 0) || 0;
  const totalAdvances = staffList?.reduce((acc, user) => acc + Number(String(user.total_advance || 0).replace(/,/g, "")), 0) || 0;
  const totalNetBudget = staffList?.reduce((acc, user) => acc + Number(String(user.net_salary || 0).replace(/,/g, "")), 0) || 0;

  const paidCount = staffList?.filter((user) => user.payroll_status === "Paid").length || 0;
  const pendingCount = (staffList?.length || 0) - paidCount;

  const remainingBudget = staffList
    ?.filter((user) => user.payroll_status !== "Paid")
    .reduce((acc, user) => acc + Number(String(user.net_salary || 0).replace(/,/g, "")), 0) || 0;

  // Filter transactions
  const incentiveAndFineTx = transactions?.filter(tx => tx.type === "incentive" || tx.type === "fine") || [];
  const advanceTx = transactions?.filter(tx => tx.type === "advance") || [];

  const handleProcessPayroll = async () => {
    try {
      setIsProcessing(true);
      toast.loading("Processing payroll...");
      const res = await processBulkPayroll();
      if (res.status === 200) {
        await mutatePayroll();
        toast.dismiss();
        toast.success(res.data.message || "Payroll processed successfully for all staff");
        document.getElementById("modal-confirm-payroll").close();
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.message || "Failed to process payroll.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResetPayroll = async () => {
    try {
      setIsProcessing(true);
      toast.loading("Resetting payroll status...");
      const res = await resetBulkPayroll();
      if (res.status === 200) {
        await mutatePayroll();
        toast.dismiss();
        toast.success(res.data.message || "Payroll reset successfully");
        document.getElementById("modal-confirm-reset").close();
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.message || "Failed to reset payroll.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleSingleStatus = async (user) => {
    const newStatus = user.payroll_status === "Paid" ? "Pending" : "Paid";
    try {
      toast.loading(`Updating status for ${user.name}...`);
      const res = await updateUserPayrollStatus(user.username, newStatus);
      if (res.status === 200) {
        await mutatePayroll();
        toast.dismiss();
        toast.success(res.data.message || `Salary marked as ${newStatus}`);
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.message || "Failed to update payroll status.");
    }
  };

  const handleAddShiftSubmit = async (e) => {
    e.preventDefault();
    if (!newShift.name || !newShift.startTime || !newShift.endTime) {
      toast.error("Please fill in all shift fields.");
      return;
    }
    try {
      setIsProcessing(true);
      toast.loading("Adding shift...");
      await addNewShift(newShift.name, newShift.startTime, newShift.endTime);
      await mutateShifts();
      toast.dismiss();
      toast.success("Shift added successfully!");
      setNewShift({ name: "", startTime: "09:00", endTime: "17:00" });
      document.getElementById("modal-add-shift").close();
    } catch (err) {
      toast.dismiss();
      toast.error(err || "Failed to add shift.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteShiftClick = async (id) => {
    if (!window.confirm("Are you sure you want to delete this shift?")) return;
    try {
      toast.loading("Deleting shift...");
      await deleteShift(id);
      await mutateShifts();
      toast.dismiss();
      toast.success("Shift deleted successfully!");
    } catch (err) {
      toast.dismiss();
      toast.error(err || "Failed to delete shift.");
    }
  };

  const handleAddLogSubmit = async (e) => {
    e.preventDefault();
    if (!newLog.username || !newLog.shiftId || !newLog.date) {
      toast.error("Please choose a staff member, shift, and date.");
      return;
    }
    try {
      setIsProcessing(true);
      toast.loading("Logging shift...");
      await addNewShiftLog(newLog.username, newLog.shiftId, newLog.date);
      await mutateShiftLogs();
      toast.dismiss();
      toast.success("Shift log added successfully!");
      document.getElementById("modal-add-log").close();
    } catch (err) {
      toast.dismiss();
      toast.error(err || "Failed to log shift.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteLogClick = async (id) => {
    if (!window.confirm("Are you sure you want to delete this log?")) return;
    try {
      toast.loading("Deleting log...");
      await deleteShiftLog(id);
      await mutateShiftLogs();
      toast.dismiss();
      toast.success("Shift log deleted successfully!");
    } catch (err) {
      toast.dismiss();
      toast.error(err || "Failed to delete log.");
    }
  };

  const handleAddTxSubmit = async (e) => {
    e.preventDefault();
    if (!newTx.username || !newTx.amount || !newTx.date) {
      toast.error("Please provide staff, amount, and date.");
      return;
    }
    try {
      setIsProcessing(true);
      toast.loading(`Recording ${newTx.type}...`);
      await addNewStaffTransaction(newTx.username, newTx.type, newTx.amount, newTx.reason, newTx.date);
      await mutateTransactions();
      await mutatePayroll(); // recalculate payroll details
      toast.dismiss();
      toast.success(`${newTx.type} recorded successfully!`);
      setNewTx({ username: "", type: "incentive", amount: "", reason: "", date: new Date().toISOString().split('T')[0] });
      document.getElementById("modal-add-tx").close();
    } catch (err) {
      toast.dismiss();
      toast.error(err || "Failed to record transaction.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddAdvanceSubmit = async (e) => {
    e.preventDefault();
    if (!newAdvance.username || !newAdvance.amount || !newAdvance.date) {
      toast.error("Please choose a staff member, amount, and date.");
      return;
    }
    try {
      setIsProcessing(true);
      toast.loading("Recording advance salary...");
      await addNewStaffTransaction(newAdvance.username, "advance", newAdvance.amount, newAdvance.reason, newAdvance.date);
      await mutateTransactions();
      await mutatePayroll(); // recalculate payroll details
      toast.dismiss();
      toast.success("Advance salary recorded successfully!");
      setNewAdvance({ username: "", amount: "", reason: "", date: new Date().toISOString().split('T')[0] });
      document.getElementById("modal-add-advance").close();
    } catch (err) {
      toast.dismiss();
      toast.error(err || "Failed to record advance salary.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteTxClick = async (id) => {
    if (!window.confirm("Are you sure you want to delete this transaction?")) return;
    try {
      toast.loading("Deleting transaction...");
      await deleteStaffTransaction(id);
      await mutateTransactions();
      await mutatePayroll(); // recalculate payroll details
      toast.dismiss();
      toast.success("Transaction deleted successfully!");
    } catch (err) {
      toast.dismiss();
      toast.error(err || "Failed to delete transaction.");
    }
  };

  return (
    <Page>
      {/* Tab controls */}
      <div className="tabs tabs-boxed mb-6 bg-restro-green/5 border border-restro-border-green p-1.5 flex flex-wrap gap-1 rounded-2xl max-w-5xl">
        <button 
          onClick={() => setActiveTab("payroll")}
          className={`tab tab-lg rounded-xl transition-all ${activeTab === "payroll" ? "bg-restro-green text-white font-bold" : "text-gray-400 hover:text-restro-text"}`}
        >
          Payroll Processing
        </button>
        <button 
          onClick={() => setActiveTab("shifts")}
          className={`tab tab-lg rounded-xl transition-all ${activeTab === "shifts" ? "bg-restro-green text-white font-bold" : "text-gray-400 hover:text-restro-text"}`}
        >
          Shift Management
        </button>
        <button 
          onClick={() => setActiveTab("logs")}
          className={`tab tab-lg rounded-xl transition-all ${activeTab === "logs" ? "bg-restro-green text-white font-bold" : "text-gray-400 hover:text-restro-text"}`}
        >
          Shift Logs
        </button>
        <button 
          onClick={() => setActiveTab("transactions")}
          className={`tab tab-lg rounded-xl transition-all ${activeTab === "transactions" ? "bg-restro-green text-white font-bold" : "text-gray-400 hover:text-restro-text"}`}
        >
          Incentives & Fines
        </button>
        <button 
          onClick={() => setActiveTab("advance")}
          className={`tab tab-lg rounded-xl transition-all ${activeTab === "advance" ? "bg-restro-green text-white font-bold" : "text-gray-400 hover:text-restro-text"}`}
        >
          Advance Salary
        </button>
      </div>

      {/* Date filter bar for data queries */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 border-b border-restro-border-green pb-4">
        <div>
          <h3 className="text-2xl font-bold text-restro-text capitalize">
            {activeTab === "payroll" ? "Payroll processing" : activeTab === "logs" ? "Shift logging" : activeTab === "transactions" ? "Incentives & fines" : activeTab === "advance" ? "Advance salary log" : "Shift settings"}
          </h3>
          <p className="text-xs text-gray-500">Period: {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input 
            type="date"
            className="input input-sm border border-restro-border-green bg-restro-card-bg text-restro-text rounded-xl"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <span className="text-xs text-gray-400">to</span>
          <input 
            type="date"
            className="input input-sm border border-restro-border-green bg-restro-card-bg text-restro-text rounded-xl"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {/* TAB CONTENT: PAYROLL */}
      {activeTab === "payroll" && (
        <div>
          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h4 className="text-lg font-bold text-restro-text">Staff Monthly Salaries</h4>
              <p className="text-sm text-gray-500">Processed based on Base Salary + Incentives - Fines - Advances.</p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => document.getElementById("modal-confirm-reset").showModal()}
                disabled={isProcessing}
                className="btn btn-outline border-restro-border-green text-restro-text hover:bg-restro-button-hover hover:border-restro-border-green rounded-xl transition active:scale-95 flex items-center gap-2"
              >
                <IconReload stroke={iconStroke} size={18} />
                Reset Status
              </button>
              
              <button
                onClick={() => document.getElementById("modal-confirm-payroll").showModal()}
                disabled={isProcessing || !staffList || staffList.length === 0}
                className="btn bg-restro-green border-none hover:bg-restro-green-hover text-white rounded-xl transition active:scale-95 flex items-center gap-2"
              >
                <IconCoin stroke={iconStroke} size={18} />
                Process Payroll
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            <div className="border rounded-2xl p-4 border-restro-border-green bg-restro-green/5 shadow-sm flex items-center gap-3">
              <div className="p-2 bg-restro-green/10 text-restro-green rounded-lg">
                <IconUsers stroke={iconStroke} size={24} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Staff</p>
                <p className="text-lg font-black text-restro-text">{staffList?.length || 0}</p>
              </div>
            </div>

            <div className="border rounded-2xl p-4 border-restro-border-green bg-restro-green/5 shadow-sm flex items-center gap-3">
              <div className="p-2 bg-restro-green/10 text-restro-green rounded-lg">
                <IconReportMoney stroke={iconStroke} size={24} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Net Budget</p>
                <p className="text-lg font-black text-restro-text">
                  {currencySymbol}{totalNetBudget.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            <div className="border rounded-2xl p-4 border-restro-border-green bg-restro-green/5 shadow-sm flex items-center gap-3">
              <div className="p-2 bg-green-500/10 text-green-500 rounded-lg">
                <IconTrendingUp stroke={iconStroke} size={24} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Incentives</p>
                <p className="text-lg font-black text-green-500">
                  +{currencySymbol}{totalIncentives.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            <div className="border rounded-2xl p-4 border-restro-border-green bg-restro-green/5 shadow-sm flex items-center gap-3">
              <div className="p-2 bg-red-500/10 text-red-500 rounded-lg">
                <IconTrendingDown stroke={iconStroke} size={24} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Fines</p>
                <p className="text-lg font-black text-red-500">
                  -{currencySymbol}{totalFines.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            <div className="border rounded-2xl p-4 border-restro-border-green bg-restro-green/5 shadow-sm flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 text-amber-500 rounded-lg">
                <IconCoin stroke={iconStroke} size={24} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Advances Paid</p>
                <p className="text-lg font-black text-amber-500">
                  -{currencySymbol}{totalAdvances.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-3xl border-restro-border-green overflow-hidden bg-restro-card-bg shadow-sm">
            <div className="overflow-x-auto">
              <table className="table w-full text-restro-text">
                <thead>
                  <tr className="border-b border-restro-border-green text-gray-400 bg-black/10">
                    <th className="py-4">Staff Member</th>
                    <th>Role</th>
                    <th>Payment Status</th>
                    <th className="text-right">Base Salary</th>
                    <th className="text-right">Incentives (+)</th>
                    <th className="text-right">Fines (-)</th>
                    <th className="text-right text-amber-500">Advances (-)</th>
                    <th className="text-right font-black text-restro-text">Net Pay</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {staffList && staffList.length > 0 ? (
                    staffList.map((staff, i) => {
                      const { username, name, role, base_salary, total_incentives, total_fines, total_advance, net_salary, payroll_status } = staff;
                      const isPaid = payroll_status === "Paid";
                      return (
                        <tr key={i} className="hover:bg-restro-button-hover/5 border-b border-restro-border-green last:border-none">
                          <td className="py-4">
                            <div className="flex items-center gap-3">
                              <div className="avatar placeholder">
                                <div className="bg-restro-green text-neutral-content rounded-full w-10 h-10 flex items-center justify-center font-bold">
                                  <span>{(name || 'User').charAt(0).toUpperCase()}</span>
                                </div>
                              </div>
                              <div>
                                <div className="font-bold text-restro-text">{name || 'User'}</div>
                                <div className="text-xs text-gray-500">{username}</div>
                              </div>
                            </div>
                          </td>
                          <td className="capitalize">
                            <span className="badge badge-outline border-gray-500 text-gray-400 text-xs px-2.5 py-1">
                              {role}
                            </span>
                          </td>
                          <td>
                            <span className={`badge border-none text-xs font-bold px-2.5 py-1 ${isPaid ? "bg-green-500/10 text-green-500" : "bg-amber-500/10 text-amber-500"}`}>
                              {payroll_status || "Pending"}
                            </span>
                          </td>
                          <td className="text-right font-mono">{currencySymbol}{Number(String(base_salary).replace(/,/g, "")).toFixed(2)}</td>
                          <td className="text-right font-mono text-green-500">+{currencySymbol}{Number(String(total_incentives).replace(/,/g, "")).toFixed(2)}</td>
                          <td className="text-right font-mono text-red-500">-{currencySymbol}{Number(String(total_fines).replace(/,/g, "")).toFixed(2)}</td>
                          <td className="text-right font-mono text-amber-500">-{currencySymbol}{Number(String(total_advance || 0).replace(/,/g, "")).toFixed(2)}</td>
                          <td className="text-right font-mono font-black text-restro-text">{currencySymbol}{Number(String(net_salary).replace(/,/g, "")).toFixed(2)}</td>
                          <td className="text-center">
                            <button
                              onClick={() => handleToggleSingleStatus(staff)}
                              className={`btn btn-xs rounded-lg transition active:scale-95 px-3 py-1 ${isPaid ? "btn-outline border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-white" : "bg-green-500 hover:bg-green-600 text-white border-none"}`}
                            >
                              {isPaid ? "Mark Pending" : "Pay Now"}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center py-10 text-gray-500">No staff found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: SHIFTS */}
      {activeTab === "shifts" && (
        <div>
          <div className="flex items-center justify-between gap-4 mb-6">
            <div>
              <h4 className="text-lg font-bold text-restro-text">Shift Settings</h4>
              <p className="text-sm text-gray-500">Define working shifts for staff assignments.</p>
            </div>
            <button 
              onClick={() => document.getElementById("modal-add-shift").showModal()}
              className="btn bg-restro-green border-none hover:bg-restro-green-hover text-white rounded-xl transition flex items-center gap-2"
            >
              <IconPlus stroke={iconStroke} size={18} />
              Add Shift
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {shifts && shifts.length > 0 ? (
              shifts.map((shift) => (
                <div key={shift.id} className="border border-restro-border-green bg-restro-card-bg rounded-2xl p-5 shadow-sm hover:shadow-md transition">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-black text-lg text-restro-text">{shift.name}</p>
                      <div className="flex items-center gap-2 text-gray-400 mt-2 text-sm">
                        <IconClock stroke={iconStroke} size={16} />
                        <span>{shift.start_time} - {shift.end_time}</span>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleDeleteShiftClick(shift.id)}
                      className="p-2 hover:bg-red-500/10 text-red-500 rounded-xl transition"
                    >
                      <IconTrash stroke={iconStroke} size={18} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full border border-dashed border-restro-border-green rounded-3xl text-center py-10 text-gray-500">
                No shifts defined yet. Click "Add Shift" to get started.
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB CONTENT: LOGS */}
      {activeTab === "logs" && (
        <div>
          <div className="flex items-center justify-between gap-4 mb-6">
            <div>
              <h4 className="text-lg font-bold text-restro-text">Shift Logs</h4>
              <p className="text-sm text-gray-500">Track and view shift logs assigned to staff members.</p>
            </div>
            <button 
              onClick={() => document.getElementById("modal-add-log").showModal()}
              className="btn bg-restro-green border-none hover:bg-restro-green-hover text-white rounded-xl transition flex items-center gap-2"
            >
              <IconCalendar stroke={iconStroke} size={18} />
              Log Shift
            </button>
          </div>

          <div className="border rounded-3xl border-restro-border-green overflow-hidden bg-restro-card-bg shadow-sm">
            <div className="overflow-x-auto">
              <table className="table w-full text-restro-text">
                <thead>
                  <tr className="border-b border-restro-border-green text-gray-400 bg-black/10">
                    <th className="py-4">Date</th>
                    <th>Staff Name</th>
                    <th>Assigned Shift</th>
                    <th>Working Hours</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {shiftLogs && shiftLogs.length > 0 ? (
                    shiftLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-restro-button-hover/5 border-b border-restro-border-green last:border-none">
                        <td className="py-4 font-mono font-bold text-restro-text">{log.date}</td>
                        <td className="font-bold text-restro-text">{log.staff_name}</td>
                        <td>
                          <span className="badge bg-restro-green/10 text-restro-green border-none text-xs px-2.5 py-1">
                            {log.shift_name}
                          </span>
                        </td>
                        <td className="font-mono text-gray-400">{log.start_time} - {log.end_time}</td>
                        <td className="text-center">
                          <button 
                            onClick={() => handleDeleteLogClick(log.id)}
                            className="p-1.5 hover:bg-red-500/10 text-red-500 rounded-lg transition"
                          >
                            <IconTrash stroke={iconStroke} size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-10 text-gray-500">No shift logs found for this period.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: TRANSACTIONS */}
      {activeTab === "transactions" && (
        <div>
          <div className="flex items-center justify-between gap-4 mb-6">
            <div>
              <h4 className="text-lg font-bold text-restro-text">Incentives & Fines Log</h4>
              <p className="text-sm text-gray-500">Manage bonuses, performance incentives, and employee deductions.</p>
            </div>
            <button 
              onClick={() => document.getElementById("modal-add-tx").showModal()}
              className="btn bg-restro-green border-none hover:bg-restro-green-hover text-white rounded-xl transition flex items-center gap-2"
            >
              <IconPlus stroke={iconStroke} size={18} />
              Record Incentive/Fine
            </button>
          </div>

          <div className="border rounded-3xl border-restro-border-green overflow-hidden bg-restro-card-bg shadow-sm">
            <div className="overflow-x-auto">
              <table className="table w-full text-restro-text">
                <thead>
                  <tr className="border-b border-restro-border-green text-gray-400 bg-black/10">
                    <th className="py-4">Date</th>
                    <th>Staff Name</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Reason</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {incentiveAndFineTx && incentiveAndFineTx.length > 0 ? (
                    incentiveAndFineTx.map((tx) => (
                      <tr key={tx.id} className="hover:bg-restro-button-hover/5 border-b border-restro-border-green last:border-none">
                        <td className="py-4 font-mono">{tx.date}</td>
                        <td className="font-bold text-restro-text">{tx.staff_name}</td>
                        <td>
                          <span className={`badge border-none text-xs font-bold px-2.5 py-1 ${tx.type === "incentive" ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"}`}>
                            {tx.type}
                          </span>
                        </td>
                        <td className={`font-black font-mono ${tx.type === "incentive" ? "text-green-500" : "text-red-500"}`}>
                          {tx.type === "incentive" ? "+" : "-"}{currencySymbol}{Number(String(tx.amount).replace(/,/g, "")).toFixed(2)}
                        </td>
                        <td className="text-sm text-gray-400">{tx.reason || "—"}</td>
                        <td className="text-center">
                          <button 
                            onClick={() => handleDeleteTxClick(tx.id)}
                            className="p-1.5 hover:bg-red-500/10 text-red-500 rounded-lg transition"
                          >
                            <IconTrash stroke={iconStroke} size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center py-10 text-gray-500">No transactions found for this period.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: ADVANCE SALARY */}
      {activeTab === "advance" && (
        <div>
          <div className="flex items-center justify-between gap-4 mb-6">
            <div>
              <h4 className="text-lg font-bold text-restro-text">Advance Salary Disbursals</h4>
              <p className="text-sm text-gray-500">Track advances paid out ahead of time. These are automatically deducted from the monthly payroll.</p>
            </div>
            <button 
              onClick={() => document.getElementById("modal-add-advance").showModal()}
              className="btn bg-restro-green border-none hover:bg-restro-green-hover text-white rounded-xl transition flex items-center gap-2"
            >
              <IconPlus stroke={iconStroke} size={18} />
              Record Advance Salary
            </button>
          </div>

          <div className="border rounded-3xl border-restro-border-green overflow-hidden bg-restro-card-bg shadow-sm">
            <div className="overflow-x-auto">
              <table className="table w-full text-restro-text">
                <thead>
                  <tr className="border-b border-restro-border-green text-gray-400 bg-black/10">
                    <th className="py-4">Date</th>
                    <th>Staff Name</th>
                    <th>Amount</th>
                    <th>Reason / Details</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {advanceTx && advanceTx.length > 0 ? (
                    advanceTx.map((tx) => (
                      <tr key={tx.id} className="hover:bg-restro-button-hover/5 border-b border-restro-border-green last:border-none">
                        <td className="py-4 font-mono">{tx.date}</td>
                        <td className="font-bold text-restro-text">{tx.staff_name}</td>
                        <td className="font-black font-mono text-amber-500">
                          -{currencySymbol}{Number(String(tx.amount).replace(/,/g, "")).toFixed(2)}
                        </td>
                        <td className="text-sm text-gray-400">{tx.reason || "—"}</td>
                        <td className="text-center">
                          <button 
                            onClick={() => handleDeleteTxClick(tx.id)}
                            className="p-1.5 hover:bg-red-500/10 text-red-500 rounded-lg transition"
                          >
                            <IconTrash stroke={iconStroke} size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-10 text-gray-500">No advance salary records found for this period.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add Shift Modal */}
      <dialog id="modal-add-shift" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box bg-restro-card-bg border border-restro-border-green">
          <h3 className="font-bold text-lg text-restro-text">Add Working Shift</h3>
          <form onSubmit={handleAddShiftSubmit} className="mt-4 flex flex-col gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-400 mb-1 block">Shift Name</label>
              <input 
                type="text"
                placeholder="e.g. Morning Shift"
                className="input input-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                value={newShift.name}
                onChange={(e) => setNewShift({ ...newShift, name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-400 mb-1 block">Start Time</label>
                <input 
                  type="time"
                  className="input input-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                  value={newShift.startTime}
                  onChange={(e) => setNewShift({ ...newShift, startTime: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-400 mb-1 block">End Time</label>
                <input 
                  type="time"
                  className="input input-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                  value={newShift.endTime}
                  onChange={(e) => setNewShift({ ...newShift, endTime: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="modal-action">
              <button 
                type="button" 
                onClick={() => document.getElementById("modal-add-shift").close()}
                className="btn btn-outline border-restro-border-green text-restro-text hover:bg-restro-button-hover"
              >
                Cancel
              </button>
              <button 
                type="submit"
                disabled={isProcessing}
                className="btn bg-restro-green hover:bg-restro-green-hover text-white border-none"
              >
                Save Shift
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* Log Shift Modal */}
      <dialog id="modal-add-log" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box bg-restro-card-bg border border-restro-border-green">
          <h3 className="font-bold text-lg text-restro-text">Log Staff Shift</h3>
          <form onSubmit={handleAddLogSubmit} className="mt-4 flex flex-col gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-400 mb-1 block">Select Staff Member</label>
              <select 
                className="select select-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                value={newLog.username}
                onChange={(e) => setNewLog({ ...newLog, username: e.target.value })}
                required
              >
                <option value="">-- Choose Staff --</option>
                {rawUsers?.map((user) => (
                  <option key={user.username} value={user.username}>{user.name} ({user.username})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-400 mb-1 block">Select Working Shift</label>
              <select 
                className="select select-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                value={newLog.shiftId}
                onChange={(e) => setNewLog({ ...newLog, shiftId: e.target.value })}
                required
              >
                <option value="">-- Choose Shift --</option>
                {shifts?.map((shift) => (
                  <option key={shift.id} value={shift.id}>{shift.name} ({shift.start_time} - {shift.end_time})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-400 mb-1 block">Work Date</label>
              <input 
                type="date"
                className="input input-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                value={newLog.date}
                onChange={(e) => setNewLog({ ...newLog, date: e.target.value })}
                required
              />
            </div>
            <div className="modal-action">
              <button 
                type="button" 
                onClick={() => document.getElementById("modal-add-log").close()}
                className="btn btn-outline border-restro-border-green text-restro-text hover:bg-restro-button-hover"
              >
                Cancel
              </button>
              <button 
                type="submit"
                disabled={isProcessing}
                className="btn bg-restro-green hover:bg-restro-green-hover text-white border-none"
              >
                Assign Shift
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* Add Incentive/Fine Modal */}
      <dialog id="modal-add-tx" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box bg-restro-card-bg border border-restro-border-green">
          <h3 className="font-bold text-lg text-restro-text">Record Staff Incentive or Fine</h3>
          <form onSubmit={handleAddTxSubmit} className="mt-4 flex flex-col gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-400 mb-1 block">Select Staff Member</label>
              <select 
                className="select select-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                value={newTx.username}
                onChange={(e) => setNewTx({ ...newTx, username: e.target.value })}
                required
              >
                <option value="">-- Choose Staff --</option>
                {rawUsers?.map((user) => (
                  <option key={user.username} value={user.username}>{user.name} ({user.username})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-400 mb-1 block">Transaction Type</label>
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2 cursor-pointer text-restro-text">
                  <input 
                    type="radio" 
                    name="txType" 
                    className="radio radio-success"
                    checked={newTx.type === "incentive"}
                    onChange={() => setNewTx({ ...newTx, type: "incentive" })}
                  />
                  <span>Incentive (+)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-restro-text">
                  <input 
                    type="radio" 
                    name="txType" 
                    className="radio radio-error"
                    checked={newTx.type === "fine"}
                    onChange={() => setNewTx({ ...newTx, type: "fine" })}
                  />
                  <span>Fine (-)</span>
                </label>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-400 mb-1 block">Amount ({currencySymbol})</label>
              <input 
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                className="input input-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                value={newTx.amount}
                onChange={(e) => setNewTx({ ...newTx, amount: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-400 mb-1 block">Reason / Details</label>
              <textarea 
                placeholder="e.g. Overtime bonus, late arrival deduction, etc."
                className="textarea textarea-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                value={newTx.reason}
                onChange={(e) => setNewTx({ ...newTx, reason: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-400 mb-1 block">Record Date</label>
              <input 
                type="date"
                className="input input-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                value={newTx.date}
                onChange={(e) => setNewTx({ ...newTx, date: e.target.value })}
                required
              />
            </div>
            <div className="modal-action">
              <button 
                type="button" 
                onClick={() => document.getElementById("modal-add-tx").close()}
                className="btn btn-outline border-restro-border-green text-restro-text hover:bg-restro-button-hover"
              >
                Cancel
              </button>
              <button 
                type="submit"
                disabled={isProcessing}
                className="btn bg-restro-green hover:bg-restro-green-hover text-white border-none"
              >
                Record Adjustment
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* Add Advance Salary Modal */}
      <dialog id="modal-add-advance" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box bg-restro-card-bg border border-restro-border-green">
          <h3 className="font-bold text-lg text-restro-text">Record Advance Salary Payment</h3>
          <form onSubmit={handleAddAdvanceSubmit} className="mt-4 flex flex-col gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-400 mb-1 block">Select Staff Member</label>
              <select 
                className="select select-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                value={newAdvance.username}
                onChange={(e) => setNewAdvance({ ...newAdvance, username: e.target.value })}
                required
              >
                <option value="">-- Choose Staff --</option>
                {rawUsers?.map((user) => (
                  <option key={user.username} value={user.username}>{user.name} ({user.username})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-400 mb-1 block">Amount ({currencySymbol})</label>
              <input 
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                className="input input-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                value={newAdvance.amount}
                onChange={(e) => setNewAdvance({ ...newAdvance, amount: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-400 mb-1 block">Reason / Details</label>
              <textarea 
                placeholder="e.g. Personal emergency advance, festival advance"
                className="textarea textarea-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                value={newAdvance.reason}
                onChange={(e) => setNewAdvance({ ...newAdvance, reason: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-400 mb-1 block">Date of Payment</label>
              <input 
                type="date"
                className="input input-bordered w-full bg-restro-card-bg border-restro-border-green text-restro-text"
                value={newAdvance.date}
                onChange={(e) => setNewAdvance({ ...newAdvance, date: e.target.value })}
                required
              />
            </div>
            <div className="modal-action">
              <button 
                type="button" 
                onClick={() => document.getElementById("modal-add-advance").close()}
                className="btn btn-outline border-restro-border-green text-restro-text hover:bg-restro-button-hover"
              >
                Cancel
              </button>
              <button 
                type="submit"
                disabled={isProcessing}
                className="btn bg-restro-green hover:bg-restro-green-hover text-white border-none"
              >
                Save & Record Disbursal
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* Confirmation Modal - Bulk Process */}
      <dialog id="modal-confirm-payroll" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box bg-restro-card-bg border border-restro-border-green">
          <h3 className="font-bold text-lg text-restro-text">Confirm Payroll Processing</h3>
          <p className="py-4 text-sm text-gray-400">
            This will initiate salary payment for all staff members who are currently marked as <strong>Pending</strong>.
            <br />
            Total disbursement amount: <strong className="text-restro-green">{currencySymbol}{remainingBudget.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>.
          </p>
          <div className="modal-action">
            <button
              onClick={() => document.getElementById("modal-confirm-payroll").close()}
              className="btn btn-outline border-restro-border-green text-restro-text hover:bg-restro-button-hover"
            >
              Cancel
            </button>
            <button
              onClick={handleProcessPayroll}
              disabled={isProcessing}
              className="btn bg-restro-green hover:bg-restro-green-hover text-white border-none rounded-xl"
            >
              Confirm & Disburse
            </button>
          </div>
        </div>
      </dialog>

      {/* Confirmation Modal - Reset */}
      <dialog id="modal-confirm-reset" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box bg-restro-card-bg border border-restro-border-green">
          <h3 className="font-bold text-lg text-restro-text">Confirm Payroll Reset</h3>
          <p className="py-4 text-sm text-gray-400">
            This will reset the payment status of all staff members back to <strong>Pending</strong>.
            Are you sure you want to proceed?
          </p>
          <div className="modal-action">
            <button
              onClick={() => document.getElementById("modal-confirm-reset").close()}
              className="btn btn-outline border-restro-border-green text-restro-text hover:bg-restro-button-hover"
            >
              Cancel
            </button>
            <button
              onClick={handleResetPayroll}
              disabled={isProcessing}
              className="btn bg-amber-500 hover:bg-amber-600 text-white border-none rounded-xl"
            >
              Yes, Reset
            </button>
          </div>
        </div>
      </dialog>
    </Page>
  );
}
